import React, { useEffect, useState } from 'react';
import {
  ClickablePageTitle,
  PageHeaderDivider,
} from '../../../components/PageHeader';
import { appRoutes } from '../../../path';
import styled from 'styled-components';
import RoleAssignTable from '../Roles/RoleAssignTable';
import { PrimaryButton, SecondaryButton } from '../../../components/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { setDictionaryListAction } from '../../../redux/slices/dictionary';
import { DictionaryTypeEnum } from '../../../utils/interfaces';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { DescriptionField, NameField } from '../../../components/Form';
import { editInfoModalLookup } from '../../../components/EditInfoModal/utils';
import { z } from 'zod';
import {
  descriptionFieldSchema,
  nameFieldSchema,
} from '../../../utils/validation';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_SINGLE_USER_GROUP_PERMISSION,
  USER_GROUP_CREATE,
  USER_GROUP_EDIT,
} from '../../../utils/apollo/permission';
import {
  IRole,
  IUserGroupCreateMutation,
  IUserGroupCreateMutationVariables,
  IUserGroupEditMutation,
  IUserGroupEditMutationVariables,
  IUserGroupPermissionQuery,
  IUserGroupPermissionQueryVariables,
  PermissionKey,
  UserGroupType,
} from '../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { SvgIcon } from '../../../components/Icon';
import KubitTooltip from '../../../components/KubitTooltip';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../PermissionControls/PermissionElementWrapper';
import toastMessage from '../../../utils/toastMessage';
import { ContentLoader } from '../../../components/Loaders';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import {
  groupCreated,
  groupUpdated,
} from '../../../utils/analytics/events/group/events';
import { assertNotNil } from '../../../utils/types';
import { formErrorViewed } from '../../../utils/analytics/events/error/events';
import { isNil } from 'lodash';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import { theme } from '../../../styles/theme';
import * as PermissionControlsUI from '../../PermissionControls/PermissionControlsUI';

const Footer = styled.div`
  display: flex;
  column-gap: 16px;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
  display: flex;
  column-gap: 8px;
  > span {
    color: ${theme.colors.secondary800};
    font-weight: 500;
  }
`;

const InputRowContainer = styled.div`
  display: flex;
  column-gap: 4px;
  align-items: center;
  > div {
    width: 600px;
  }
`;

const validationSchema = (nameMaxLimit: number) =>
  z.object({
    name: nameFieldSchema(nameMaxLimit),
    description: descriptionFieldSchema,
  });

/**
 * UserGroupModify is the page for creating and editing user group.
 */
const UserGroupModify = () => {
  const { track } = useAnalytics();
  const { checkIsPermitted } = usePermission();
  const isPermittedToEdit = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AdminEdit],
  });
  const isPermittedToCreate = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AdminCreate],
  });
  const urlParams = useParams();
  const editingUserGroupId = urlParams.userGroupId;
  const isEditingUserGroup = !isNil(editingUserGroupId);

  useEffect(() => {
    track(
      pageViewed({
        category: 'settings',
        name: isEditingUserGroup ? 'edit group' : 'create group',
        id: editingUserGroupId,
      }),
    );
  }, [editingUserGroupId, isEditingUserGroup, track]);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const selectedRoles = useAppSelector(
    (state) => state.dictionary.roles,
  ) as IRole[];
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [isExternalGroup, setIsExternalGroup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const onCreateUserGroup = ({ userGroupCreate }: IUserGroupCreateMutation) => {
    const { entity, errors } = userGroupCreate;
    if (printGraphqlErrors(errors)) {
      return;
    }

    assertNotNil(entity);
    toastMessage({
      message: `${entity.name} has been created.`,
      type: 'info',
    });
    track(groupCreated(entity));
    navigate(appRoutes.permission.userGroups + `/${entity.id}`);
  };

  const onEditUserGroup = ({ userGroupEdit }: IUserGroupEditMutation) => {
    const { entity, errors } = userGroupEdit;
    if (printGraphqlErrors(errors)) {
      return;
    }
    assertNotNil(entity);
    toastMessage({
      message: `${entity.name} has been updated.`,
      type: 'info',
    });
    track(groupUpdated(entity));
    navigate(appRoutes.permission.userGroups + `/${entity.id}`);
  };
  const [createUserGroup, { loading }] = useMutation<
    IUserGroupCreateMutation,
    IUserGroupCreateMutationVariables
  >(USER_GROUP_CREATE, {
    onCompleted: onCreateUserGroup,
  });

  const [updateUserGroup, { loading: updateLoading }] = useMutation<
    IUserGroupEditMutation,
    IUserGroupEditMutationVariables
  >(USER_GROUP_EDIT, { onCompleted: onEditUserGroup });
  const onFetchUserGroup = ({ userGroup }: IUserGroupPermissionQuery) => {
    const { name, description, roles, type } = userGroup;
    setName(name);
    setDescription(description ?? '');
    setIsExternalGroup(type === UserGroupType.External);
    dispatch(
      setDictionaryListAction({
        newValues: roles,
        dictionaryType: DictionaryTypeEnum.Roles,
      }),
    );
  };

  // fetch user group info if editing
  const { loading: singleUserGroupLoading } = useQuery<
    IUserGroupPermissionQuery,
    IUserGroupPermissionQueryVariables
  >(GET_SINGLE_USER_GROUP_PERMISSION, {
    variables: {
      id: editingUserGroupId!,
    },
    onCompleted: onFetchUserGroup,
    skip: !editingUserGroupId,
  });
  const config = isEditingUserGroup
    ? editInfoModalLookup.editUserGroup
    : editInfoModalLookup.createUserGroup;
  const safeParse = validationSchema(config.nameLimit).safeParse({
    name,
    description,
  });
  const isFormValid = safeParse.success;
  const errors = !isFormValid ? safeParse?.error?.format() : null;

  const handleSubmit = async () => {
    setSubmitting(true);
    if (isFormValid) {
      const roleIds = selectedRoles.map((role) => role.id);
      const { name, description } = safeParse.data;
      if (isEditingUserGroup) {
        await updateUserGroup({
          variables: {
            input: {
              groupId: editingUserGroupId,
              roleIds,
              name: isExternalGroup ? null : name,
              description: isExternalGroup ? null : description ?? '',
            },
          },
        });
      } else {
        await createUserGroup({
          variables: {
            input: {
              name,
              description: description ?? '',
              roleIds,
            },
          },
        });
      }
    } else {
      track(formErrorViewed(config.analyticsErrorProducer(errors)));
    }
  };

  const getTitleList = () => {
    if (!isEditingUserGroup) {
      return [
        { name: 'Groups', path: appRoutes.permission.userGroups },
        { name: 'Create Group', path: '' },
      ];
    } else {
      return [
        { name: 'Groups', path: appRoutes.permission.userGroups },
        {
          name: name ?? 'Loading...',
          path: appRoutes.permission.userGroups + `/${editingUserGroupId}`,
        },
        { name: 'Edit', path: '' },
      ];
    }
  };

  useEffect(() => {
    return () => {
      dispatch(
        setDictionaryListAction({
          newValues: [],
          dictionaryType: DictionaryTypeEnum.Roles,
        }),
      );
    };
  }, [dispatch]);
  if (singleUserGroupLoading) {
    return <ContentLoader />;
  }
  return (
    <>
      <ClickablePageTitle titleList={getTitleList()} />
      <PageHeaderDivider />
      <PermissionControlsUI.OverviewContainer>
        <Title>
          {config.header}
          <span>[Unsaved]</span>
        </Title>
        <InputRowContainer>
          <NameField
            label={config.nameLabel}
            value={name}
            targetErrorElement={config.targetElement}
            maxLength={config.nameLimit}
            onChange={setName}
            hasError={submitting && Boolean(errors?.name)}
            disabled={isExternalGroup}
            noMargin={true}
          />
          {isExternalGroup && (
            <KubitTooltip
              noOpenDelay={true}
              content={'External group is not editable.'}
            >
              <SvgIcon
                name="locked"
                customFill={theme.colors.secondary900}
                height={16}
                width={16}
              />
            </KubitTooltip>
          )}
        </InputRowContainer>
        <InputRowContainer>
          <DescriptionField
            label={config.descriptionLabel}
            value={description}
            targetErrorElement={config.targetElement}
            onChange={setDescription}
            disabled={isExternalGroup}
            noMargin={true}
          />
          {/* External Group is not editable in current version, but available in Design, so keep the design for now*/}
          {isExternalGroup && (
            <KubitTooltip
              noOpenDelay={true}
              content={'External group is not editable.'}
            >
              <SvgIcon
                name="locked"
                customFill={theme.colors.secondary900}
                height={16}
                width={16}
              />
            </KubitTooltip>
          )}
        </InputRowContainer>
        <RoleAssignTable userRoleIds={[]} />
        <Footer>
          <PermissionElementWrapper
            isPermitted={
              isEditingUserGroup ? isPermittedToEdit : isPermittedToCreate
            }
          >
            <PrimaryButton
              disabled={
                isEditingUserGroup ? !isPermittedToEdit : !isPermittedToCreate
              }
              onClick={handleSubmit}
              isGreyedOut={!isFormValid}
              loading={loading || updateLoading}
              trackContext="groups"
              trackAction={isEditingUserGroup ? 'save' : 'create'}
            >
              {isEditingUserGroup ? 'Save' : 'Create'}
            </PrimaryButton>
          </PermissionElementWrapper>
          <SecondaryButton
            onClick={() =>
              navigate(
                appRoutes.permission.userGroups +
                  (editingUserGroupId ? `/${editingUserGroupId}` : ''),
              )
            }
            trackContext="groups"
            trackAction="cancel"
          >
            Cancel
          </SecondaryButton>
        </Footer>
      </PermissionControlsUI.OverviewContainer>
    </>
  );
};

export default UserGroupModify;
